import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Lock, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { publicParentApi, setStoredParentSession, getStoredParentToken } from "@/lib/parentApi";
import { useLanguage } from "@/context/LanguageContext";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
const LOGO_URL = "/images/ym6gev0b_AE8ECFCA-9BBA-44D9-A534-DED3210EC5A4.png";

// Single login screen for parents. If the email matches a registration but no
// account exists yet, the backend creates the account using the typed password
// in the same call. Removes the "do I already have an account?" confusion.
export default function ParentLogin() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot
  const [turnstileToken, setTurnstileToken] = useState("");
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    if (getStoredParentToken()) {
      navigate("/parent/dashboard", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    let cancelled = false;
    const tryRender = () => {
      if (cancelled) return;
      if (window.turnstile && containerRef.current && widgetIdRef.current === null) {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: "auto",
          callback: (token) => setTurnstileToken(token),
          "error-callback": () => setTurnstileToken(""),
          "expired-callback": () => setTurnstileToken(""),
        });
      } else if (!window.turnstile) {
        setTimeout(tryRender, 200);
      }
    };
    tryRender();
    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current !== null) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (e) {}
        widgetIdRef.current = null;
      }
    };
  }, []);

  const resetCaptcha = () => {
    if (window.turnstile && widgetIdRef.current !== null) {
      try { window.turnstile.reset(widgetIdRef.current); } catch (e) {}
      setTurnstileToken("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      toast.error(t("parent.pleaseCaptcha"));
      return;
    }
    if (password.length < 8) {
      toast.error(t("parent.pwTooShort"));
      return;
    }
    setLoading(true);
    try {
      const { data } = await publicParentApi.post("/parent/login", {
        email,
        password,
        website,
        turnstile_token: turnstileToken,
      });
      setStoredParentSession(data.token, data.email);
      toast.success(t("parent.welcomeBack"));
      navigate("/parent/dashboard", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      toast.error(msg);
      resetCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-burns-black flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <img src={LOGO_URL} alt="Burns Elite Baseball" className="h-32 sm:h-36 mx-auto mb-4" />
          <h1 className="text-2xl font-heading font-bold text-white">{t("parent.portalTitle")}</h1>
          <p className="text-zinc-400 text-sm mt-1">{t("parent.portalSubtitle")}</p>
        </div>

        <div className="bg-burns-dark/50 border border-burns-gold/20 rounded-md p-3 mb-5">
          <p className="text-zinc-300 text-xs leading-relaxed">
            <span className="text-burns-gold font-medium">{t("parent.firstTime")}</span> {t("parent.firstTimeBody")}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
          {/* Honeypot */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
            <label htmlFor="website-field">Website</label>
            <input id="website-field" type="text" name="website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-white">{t("parent.email")}</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-zinc-800 border-zinc-700 text-white"
              placeholder="parent@example.com"
              data-testid="parent-email"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-white">{t("parent.password")}</Label>
            <div className="relative">
              <Input
                id="password"
                name="parent-password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white pr-10"
                placeholder={t("parent.passwordHint")}
                data-testid="parent-password"
              />
              <button type="button" onClick={() => setShowPassword((s) => !s)} className="absolute inset-y-0 right-2 flex items-center text-zinc-500 hover:text-zinc-300" tabIndex={-1}>
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {TURNSTILE_SITE_KEY && <div ref={containerRef} className="flex justify-center" />}

          <Button
            type="submit"
            disabled={loading || !email || !password || (TURNSTILE_SITE_KEY && !turnstileToken)}
            className="w-full bg-burns-red hover:bg-burns-red/80 text-white"
            data-testid="parent-submit"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
            {t("parent.signIn")}
          </Button>

          <div className="flex items-center justify-between text-sm pt-2">
            <Link to="/parent/forgot-password" className="text-burns-gold hover:underline">{t("parent.forgotLink")}</Link>
            <Link to="/" className="text-zinc-400 hover:text-white">{t("parent.backToSite")}</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
